<template>
  <div class="table-content qaqc-consistency-check-wrap">
    <div class="table-wrap">
      <div class="table-title-div mt-4">
        <p class="table-title">{{ consistencySectionTitle }}</p>
        <div class="qaqc-scorecard-header-actions">
          <label v-if="expandableRowCount" class="qaqc-expand-all-wrap">
            <input
              ref="expandAllCheckboxInput"
              type="checkbox"
              class="qaqc-expand-all-checkbox"
              aria-label="Expand all rows with non-compliant samples"
              @change="onExpandAllCheckboxChange"
            />
            <span class="qaqc-expand-all-text">Expand all</span>
          </label>
            <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
          <v-btn
            icon
            variant="text"
            size="medium"
            class="qaqc-scorecard-collapse-btn"
            :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
            @click="isCollapsed = !isCollapsed"
          >
            <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-show="!isCollapsed" class="qaqc-consistency-check-body">
        <!-- Cross-Test Property Consistency -->
        <div v-if="crossTestRows.length" class="qaqc-consistency-subsection">
          <h6 class="qaqc-consistency-subtitle">{{ crossTestSubtitle }}</h6>
          <p class="qaqc-consistency-desc">{{ crossTestDescription }}</p>
          <div class="qaqc-consistency-table-wrap">
            <table class="qaqc-consistency-table">
              <thead>
                <tr class="qaqc-consistency-thead">
                  <th class="qaqc-consistency-th-property">Expected Property Consistency Between Tests</th>
                  <th class="qaqc-consistency-th-num">Compliant</th>
                  <th class="qaqc-consistency-th-num">Non-Compliant</th>
                  <th class="qaqc-consistency-th-num">Compliance Rate</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(item, idx) in crossTestRowsWithChips" :key="'cross-parent-' + idx">
                  <tr class="qaqc-consistency-tr">
                    <td class="qaqc-consistency-td-property">
                      <div class="qaqc-consistency-property-label">{{ formatConsistencyLabel(item.row.displayName) }}</div>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.compliant }}</td>
                    <td
                      class="qaqc-consistency-td-num qaqc-consistency-nc-cell"
                      :class="{ 'qaqc-consistency-nc-cell--interactive': rowHasNonCompliant(item.row) }"
                      role="button"
                      :tabindex="rowHasNonCompliant(item.row) ? 0 : -1"
                      @click="rowHasNonCompliant(item.row) ? toggleCrossExpand(idx) : null"
                      @keydown.enter.prevent="rowHasNonCompliant(item.row) ? toggleCrossExpand(idx) : null"
                      @keydown.space.prevent="rowHasNonCompliant(item.row) ? toggleCrossExpand(idx) : null"
                    >
                      <span class="qaqc-consistency-nc-inner">
                        <v-icon
                          v-if="rowHasNonCompliant(item.row)"
                          size="small"
                          class="qaqc-consistency-nc-chevron"
                        >{{ expandedCrossIndices.includes(idx) ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                        <span class="qaqc-consistency-nc-value">{{ item.row.nonCompliant }}</span>
                      </span>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.complianceRate }}</td>
                  </tr>
                  <tr
                    v-if="expandedCrossIndices.includes(idx) && rowHasNonCompliant(item.row)"
                    class="qaqc-consistency-expand-row"
                  >
                    <td colspan="4" class="qaqc-consistency-expand-cell">
                      <div class="qaqc-consistency-expand-inner">
                        <span class="qaqc-consistency-sublabel">Non-compliant samples (click to view QC details)</span>
                        <div v-if="item.chips.length" class="qaqc-consistency-chips">
                          <span
                            v-for="(chip, cidx) in item.chips"
                            :key="consistencyChipDomKey('cross', idx, chip, cidx)"
                            class="qaqc-consistency-chip qaqc-consistency-check-chip"
                            :class="consistencyChipClasses('cross', idx, chip, cidx)"
                            role="button"
                            :tabindex="consistencyChipTabindex('cross', idx, chip, cidx)"
                            :aria-busy="consistencyChipIsLoading('cross', idx, chip, cidx)"
                            @click.stop="openSampleQc(chip, 'cross', idx, cidx)"
                            @keydown.enter.prevent="activateConsistencyChip(chip, 'cross', idx, cidx)"
                            @keydown.space.prevent="activateConsistencyChip(chip, 'cross', idx, cidx)"
                          >
                            <v-progress-circular
                              v-if="consistencyChipIsLoading('cross', idx, chip, cidx)"
                              indeterminate
                              size="18"
                              width="2"
                              color="primary"
                              class="qaqc-consistency-check-chip-loader"
                            />
                            <template v-else>{{ chipLabel(chip) }}</template>
                          </span>
                        </div>
                        <div v-else class="qaqc-consistency-no-ids">No sample IDs returned for this check.</div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Property Ordering Hierarchy -->
        <div v-if="orderingRows.length" class="qaqc-consistency-subsection">
          <h6 class="qaqc-consistency-subtitle">Property Ordering Hierarchy</h6>
          <p class="qaqc-consistency-desc">Percentage of samples meeting expected property ordering across test types. Click on non-compliant count to see affected samples.</p>
          <div class="qaqc-consistency-table-wrap">
            <table class="qaqc-consistency-table">
              <thead>
                <tr class="qaqc-consistency-thead">
                  <th class="qaqc-consistency-th-property">Expected Property Consistency Between Tests</th>
                  <th class="qaqc-consistency-th-num">Compliant</th>
                  <th class="qaqc-consistency-th-num">Non-Compliant</th>
                  <th class="qaqc-consistency-th-num">Compliance Rate</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(item, idx) in orderingRowsWithChips" :key="'order-parent-' + idx">
                  <tr class="qaqc-consistency-tr">
                    <td class="qaqc-consistency-td-property">
                      <div class="qaqc-consistency-property-label">{{ formatConsistencyLabel(item.row.displayName) }}</div>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.compliant }}</td>
                    <td
                      class="qaqc-consistency-td-num qaqc-consistency-nc-cell"
                      :class="{ 'qaqc-consistency-nc-cell--interactive': rowHasNonCompliant(item.row) }"
                      role="button"
                      :tabindex="rowHasNonCompliant(item.row) ? 0 : -1"
                      @click="rowHasNonCompliant(item.row) ? toggleOrderExpand(idx) : null"
                      @keydown.enter.prevent="rowHasNonCompliant(item.row) ? toggleOrderExpand(idx) : null"
                      @keydown.space.prevent="rowHasNonCompliant(item.row) ? toggleOrderExpand(idx) : null"
                    >
                      <span class="qaqc-consistency-nc-inner">
                        <v-icon
                          v-if="rowHasNonCompliant(item.row)"
                          size="small"
                          class="qaqc-consistency-nc-chevron"
                        >{{ expandedOrderIndices.includes(idx) ? 'mdi-chevron-up' : 'mdi-chevron-right' }}</v-icon>
                        <span class="qaqc-consistency-nc-value">{{ item.row.nonCompliant }}</span>
                      </span>
                    </td>
                    <td class="qaqc-consistency-td-num">{{ item.row.complianceRate }}</td>
                  </tr>
                  <tr
                    v-if="expandedOrderIndices.includes(idx) && rowHasNonCompliant(item.row)"
                    class="qaqc-consistency-expand-row"
                  >
                    <td colspan="4" class="qaqc-consistency-expand-cell">
                      <div class="qaqc-consistency-expand-inner">
                        <span class="qaqc-consistency-sublabel">Non-compliant samples (click to view QC details)</span>
                        <div v-if="item.chips.length" class="qaqc-consistency-chips">
                          <span
                            v-for="(chip, cidx) in item.chips"
                            :key="consistencyChipDomKey('order', idx, chip, cidx)"
                            class="qaqc-consistency-chip qaqc-consistency-check-chip"
                            :class="consistencyChipClasses('order', idx, chip, cidx)"
                            role="button"
                            :tabindex="consistencyChipTabindex('order', idx, chip, cidx)"
                            :aria-busy="consistencyChipIsLoading('order', idx, chip, cidx)"
                            @click.stop="openSampleQc(chip, 'order', idx, cidx)"
                            @keydown.enter.prevent="activateConsistencyChip(chip, 'order', idx, cidx)"
                            @keydown.space.prevent="activateConsistencyChip(chip, 'order', idx, cidx)"
                          >
                            <v-progress-circular
                              v-if="consistencyChipIsLoading('order', idx, chip, cidx)"
                              indeterminate
                              size="18"
                              width="2"
                              color="primary"
                              class="qaqc-consistency-check-chip-loader"
                            />
                            <template v-else>{{ chipLabel(chip) }}</template>
                          </span>
                        </div>
                        <div v-else class="qaqc-consistency-no-ids">No sample IDs returned for this check.</div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="!allRows.length" class="qaqc-consistency-empty">No consistency check data available.</div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { csvEscapeCell } from "./distributionCsvHelpers.js";
import {
  matrixContextToApiFilters,
  routerQueryFromApiFilters,
  pickSamplesListQueryForRouter,
} from "./qaqcSamplesListQuery.js";
import {
  resolveQaqcFailedSampleNavigation,
  pickTestFromSamplePayload,
  pushQaqcOverviewFailedSampleRoute,
} from "./qaqcFailedSampleNavigation.js";

export default {
  name: "ConsistencyCheckSection",
  props: {
    crossTestRows: { type: Array, default: () => [] },
    orderingRows: { type: Array, default: () => [] },
    matrixTestContext: {
      type: String,
      default: "dl",
      validator: (v) => ["dl", "separator"].includes(v),
    },
  },
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      isCollapsed: false,
      expandedCrossIndices: [],
      expandedOrderIndices: [],
      loadingConsistencyChipKey: null,
    };
  },
  computed: {
    isSeparatorContext() {
      return this.matrixTestContext === "separator";
    },
    consistencySectionTitle() {
      return this.isSeparatorContext
        ? "Separator Test Properties Consistency Check"
        : "DL Properties Consistency Check";
    },
    crossTestSubtitle() {
      return this.isSeparatorContext
        ? "Cross-Test Property Consistency (Separator Test vs CCE)"
        : "Cross-Test Property Consistency (DL vs CCE)";
    },
    crossTestDescription() {
      return this.isSeparatorContext
        ? "Compares key properties between Separator Test and CCE tests for the same sample. Psat and Oil Density @ Psat should be consistent across both test types. Click on non-compliant count to see affected samples."
        : "Compares key properties between DL and CCE tests for the same sample. Psat and Oil Density @ Psat should be consistent across both test types. Click on non-compliant count to see affected samples.";
    },
    consistencyCsvFileName() {
      return this.isSeparatorContext
        ? "separator_test_properties_consistency_check.csv"
        : "dl_properties_consistency_check.csv";
    },
    navTestKind() {
      return this.matrixTestContext === "separator" ? "separator" : "dl";
    },
    allRows() {
      return [...(this.crossTestRows || []), ...(this.orderingRows || [])];
    },
    crossTestRowsWithChips() {
      return this.crossTestRows.map((row) => ({
        row,
        chips: this.buildChips(row),
      }));
    },
    orderingRowsWithChips() {
      return this.orderingRows.map((row) => ({
        row,
        chips: this.buildChips(row),
      }));
    },
    expandableRowCount() {
      let n = 0;
      this.crossTestRowsWithChips.forEach((item) => {
        if (this.rowHasNonCompliant(item.row)) n += 1;
      });
      this.orderingRowsWithChips.forEach((item) => {
        if (this.rowHasNonCompliant(item.row)) n += 1;
      });
      return n;
    },
    allExpandableExpanded() {
      const crossIdx = [];
      this.crossTestRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) crossIdx.push(idx);
      });
      const orderIdx = [];
      this.orderingRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) orderIdx.push(idx);
      });
      if (crossIdx.length + orderIdx.length === 0) return false;
      const allCross = crossIdx.every((i) => this.expandedCrossIndices.includes(i));
      const allOrder = orderIdx.every((i) => this.expandedOrderIndices.includes(i));
      return allCross && allOrder;
    },
    expandAllIndeterminate() {
      const crossIdx = [];
      this.crossTestRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) crossIdx.push(idx);
      });
      const orderIdx = [];
      this.orderingRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) orderIdx.push(idx);
      });
      const total = crossIdx.length + orderIdx.length;
      if (total < 2) return false;
      let open = 0;
      crossIdx.forEach((i) => {
        if (this.expandedCrossIndices.includes(i)) open += 1;
      });
      orderIdx.forEach((i) => {
        if (this.expandedOrderIndices.includes(i)) open += 1;
      });
      return open > 0 && open < total;
    },
  },
  watch: {
    crossTestRows() {
      this.expandedCrossIndices = [];
      this.expandedOrderIndices = [];
      this.loadingConsistencyChipKey = null;
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    orderingRows() {
      this.expandedCrossIndices = [];
      this.expandedOrderIndices = [];
      this.loadingConsistencyChipKey = null;
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandableRowCount() {
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    expandedCrossIndices: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncExpandAllCheckboxDom());
      },
    },
    expandedOrderIndices: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncExpandAllCheckboxDom());
      },
    },
  },
  mounted() {
    this.$nextTick(() => this.syncExpandAllCheckboxDom());
  },
  methods: {
    formatConsistencyLabel(text) {
      const raw = String(text ?? "").trim();
      if (!raw || !this.isSeparatorContext) return raw || text;
      return raw.replace(/\bDL\b/g, "Separator Test");
    },
    nonCompliantCountNum(row) {
      const raw = row?.nonCompliant;
      if (raw === undefined || raw === null || raw === "-") return 0;
      const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(n) ? 0 : n;
    },
    rowHasNonCompliant(row) {
      return this.nonCompliantCountNum(row) > 0;
    },
    toggleCrossExpand(idx) {
      const item = this.crossTestRowsWithChips[idx];
      if (!item || !this.rowHasNonCompliant(item.row)) return;
      const pos = this.expandedCrossIndices.indexOf(idx);
      if (pos === -1) this.expandedCrossIndices.push(idx);
      else this.expandedCrossIndices.splice(pos, 1);
    },
    toggleOrderExpand(idx) {
      const item = this.orderingRowsWithChips[idx];
      if (!item || !this.rowHasNonCompliant(item.row)) return;
      const pos = this.expandedOrderIndices.indexOf(idx);
      if (pos === -1) this.expandedOrderIndices.push(idx);
      else this.expandedOrderIndices.splice(pos, 1);
    },
    syncExpandAllCheckboxDom() {
      const el = this.$refs.expandAllCheckboxInput;
      if (!el) return;
      el.indeterminate = this.expandAllIndeterminate;
      el.checked = this.allExpandableExpanded;
    },
    onExpandAllCheckboxChange(e) {
      const cross = [];
      this.crossTestRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) cross.push(idx);
      });
      const order = [];
      this.orderingRowsWithChips.forEach((item, idx) => {
        if (this.rowHasNonCompliant(item.row)) order.push(idx);
      });
      if (cross.length + order.length === 0) return;
      if (e.target.checked) {
        this.expandedCrossIndices = [...cross];
        this.expandedOrderIndices = [...order];
      } else {
        this.expandedCrossIndices = [];
        this.expandedOrderIndices = [];
      }
      this.$nextTick(() => this.syncExpandAllCheckboxDom());
    },
    buildChips(row) {
      const fs = row?.failedSamples;
      if (Array.isArray(fs) && fs.length) return fs;
      return (row?.fluidSampleIds || []).map((fid) => ({ fluidSampleID: String(fid), id: "" }));
    },
    consistencyChipInstanceKey(section, rowIdx, chip, chipIdx) {
      return `${section}:r${rowIdx}:i${chipIdx}:${String(chip.id ?? "")}:f${String(chip.fluidSampleID ?? "")}`;
    },
    consistencyChipDomKey(section, rowIdx, chip, chipIdx) {
      return this.consistencyChipInstanceKey(section, rowIdx, chip, chipIdx);
    },
    consistencyChipIsLoading(section, rowIdx, chip, chipIdx) {
      return this.loadingConsistencyChipKey === this.consistencyChipInstanceKey(section, rowIdx, chip, chipIdx);
    },
    consistencyChipIsBusyOther(section, rowIdx, chip, chipIdx) {
      return this.loadingConsistencyChipKey != null && !this.consistencyChipIsLoading(section, rowIdx, chip, chipIdx);
    },
    consistencyChipClasses(section, rowIdx, chip, chipIdx) {
      return {
        "qaqc-consistency-check-chip--loading": this.consistencyChipIsLoading(section, rowIdx, chip, chipIdx),
        "qaqc-consistency-check-chip--disabled": this.consistencyChipIsBusyOther(section, rowIdx, chip, chipIdx),
      };
    },
    consistencyChipTabindex(section, rowIdx, chip, chipIdx) {
      if (this.consistencyChipIsLoading(section, rowIdx, chip, chipIdx)) return -1;
      if (this.consistencyChipIsBusyOther(section, rowIdx, chip, chipIdx)) return -1;
      return 0;
    },
    chipLabel(chip) {
      return chip.fluidSampleID || chip.id || "";
    },
    resolveTenantId() {
      return getTenantPrefix() || "";
    },
    pushFailedSampleTestRoute(sampleId, routeTestType, testId, wellName, navigationTarget) {
      const listQuery = {
        ...routerQueryFromApiFilters(matrixContextToApiFilters(this.navTestKind)),
        ...pickSamplesListQueryForRouter(this.$route.query || {}),
      };
      pushQaqcOverviewFailedSampleRoute(this.$router, {
        tenantId: this.resolveTenantId(),
        mongoSampleId: sampleId,
        testId,
        routeTestType,
        well: wellName,
        navigationTarget,
        listQuery,
      });
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async fetchSamplePayloadForChip(chip) {
      if (chip.id) {
        const res = await api.get(`public/api/samples/${chip.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = chip.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    activateConsistencyChip(chip, section, rowIdx, chipIdx) {
      if (this.loadingConsistencyChipKey) return;
      this.openSampleQc(chip, section, rowIdx, chipIdx);
    },
    async openSampleQc(chip, section, rowIdx, chipIdx) {
      const key = this.consistencyChipInstanceKey(section, rowIdx, chip, chipIdx);
      if (!key || this.loadingConsistencyChipKey) return;
      this.loadingConsistencyChipKey = key;
      try {
        const s = await this.fetchSamplePayloadForChip(chip);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        const { testId, routeTestType } = pickTestFromSamplePayload(s, this.navTestKind);
        const navigationTarget = resolveQaqcFailedSampleNavigation({
          source: "dlConsistencyCheck",
          testKind: this.navTestKind,
        });

        if (testId && routeTestType) {
          const well = s.location?.well ?? s.wellName ?? s.well;
          this.pushFailedSampleTestRoute(mongoId, routeTestType, testId, well, navigationTarget);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          const q = {
            type: "wells",
            ...routerQueryFromApiFilters(matrixContextToApiFilters(this.navTestKind)),
            ...pickSamplesListQueryForRouter(this.$route.query || {}),
          };
          if (well) {
            this.$router.push({ name: "QAQCDetailView", params: { well: String(well) }, query: q });
          } else {
            this.$router.push({ name: "QAQCDetailView", query: q });
          }
        }
      } catch {
        void 0;
      } finally {
        this.loadingConsistencyChipKey = null;
      }
    },
    downloadTableCSV() {
      if (!this.allRows.length) return;
      const fileName = this.consistencyCsvFileName;
      const csvHeader = ["Expected Property Consistency Between Tests", "Compliant", "Non-Compliant", "Compliance Rate", "Sample IDs"];
      const csvRows = this.allRows.map((row) =>
        [
          csvEscapeCell(this.formatConsistencyLabel(row.displayName ?? "")),
          csvEscapeCell(row.compliant ?? ""),
          csvEscapeCell(row.nonCompliant ?? ""),
          csvEscapeCell(row.complianceRate ?? ""),
          csvEscapeCell((row.fluidSampleIds || []).join(", ")),
        ].join(",")
      );
      const csvContent =
        "\ufeff" + [csvHeader.map(csvEscapeCell).join(","), ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    },
  },
};
</script>
